/**
 * Educational Videos ("How to use" tutorials) — data layer.
 *
 * Backs the platform-admin tab at components/admin/EducationalVideosTab.tsx and
 * the player at components/help/HelpVideoBox.tsx. One row per topic in
 * `help_videos`; the files live in the PUBLIC `help-videos` storage bucket.
 *
 * Two rules this module exists to enforce, both learned from bugs elsewhere in
 * the admin panel:
 *
 * 1. EVERY mutation ends in `.select()` and asserts a row came back. PostgREST
 *    answers an UPDATE that RLS filtered to zero rows with 204 and `error:
 *    null` — indistinguishable from success. Without the returned row, a
 *    non-admin who reached the panel would see "saved!" on every action while
 *    the database ignored them. See services/workspaceUsers.ts:25 for the same
 *    reasoning applied to reads.
 *
 * 2. Replacing or clearing a video deletes the OLD storage object. That is what
 *    `src_path` / `poster_path` are for — a public URL cannot be turned back
 *    into a storage path reliably, so we persist the path alongside it. Without
 *    this, every replacement would silently leak the previous upload and the
 *    bucket would grow forever.
 */
import { supabase } from './supabase';
import {
    resumableUpload, uniqueObjectPath, UploadTooLargeForServer, UploadNotSignedIn,
} from './resumableUpload';
import { HELP_TOPICS, type HelpTopic } from '../components/help/helpVideos';

const BUCKET = 'help-videos';

/** Matches the help-videos bucket ceiling (2 GB). Checked client-side so an
 *  oversized pick fails instantly instead of after an hour of uploading.
 *
 *  This is NOT the limit most people hit first: Supabase enforces a
 *  project-wide "Upload file size limit" (Settings -> Storage) above every
 *  bucket, defaulting to 50 MB, and the lower of the two wins. */
export const MAX_VIDEO_BYTES = 2 * 1024 * 1024 * 1024;
export const MAX_POSTER_BYTES = 2 * 1024 * 1024;

/** Kept here rather than in the component so the input's maxLength and the
 *  validation that rejects the save cannot drift apart. */
export const MAX_TITLE_LENGTH = 120;

/** Formats the browser accepts in a <video> tag without a transcode step.
 *  .mov is included because it is what a Mac screen recording produces by
 *  default, and Safari plays it — but it is listed last because Chrome and
 *  Firefox often cannot, which is why the UI warns on it. */
export const VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'] as const;
export const POSTER_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** .mov plays in Safari but usually not in Chrome/Firefox. Not rejected — the
 *  operator may know their audience — but the tab surfaces a warning. */
export const RISKY_VIDEO_MIME_TYPES = ['video/quicktime'] as const;

/**
 * Failures this layer can produce on purpose, as CODES rather than sentences.
 *
 * The panel is Spanish-first. If this module threw `new Error('That video is
 * 62.0 MB, over the 50 MB limit.')`, the tab could only forward that English
 * string — and it would, because a real message always wins over a fallback.
 * Codes let the caller translate; `vars` carries the numbers the sentence needs.
 */
export type HelpVideoErrorCode =
    | 'file-empty'
    | 'file-too-big'
    | 'file-type'
    | 'title-empty'
    | 'title-too-long'
    | 'no-permission'
    /** The SERVER refused the size (a 413), which is a project setting rather
     *  than a bug — see UploadTooLargeForServer. */
    | 'server-limit'
    | 'not-signed-in';

export class HelpVideoError extends Error {
    constructor(
        public readonly code: HelpVideoErrorCode,
        public readonly vars: Record<string, string | number> = {},
    ) {
        // The message is a developer-facing fallback for logs and stack traces.
        // Nothing user-visible should read it — callers switch on `code`.
        super(`help-video:${code}`);
        this.name = 'HelpVideoError';
    }
}

export interface HelpVideoRow {
    topic: HelpTopic;
    title: string;
    /** '' means "no video yet" — the player shows its placeholder. Never null. */
    src: string;
    poster: string | null;
    srcPath: string | null;
    posterPath: string | null;
    updatedAt: string;
    updatedBy: string | null;
}

interface HelpVideoDbRow {
    topic: string;
    title: string | null;
    src: string | null;
    poster: string | null;
    src_path: string | null;
    poster_path: string | null;
    updated_at: string;
    updated_by: string | null;
}

const KNOWN_TOPICS = new Set<string>(HELP_TOPICS);

function toRow(r: HelpVideoDbRow): HelpVideoRow {
    return {
        topic: r.topic as HelpTopic,
        title: r.title ?? '',
        src: r.src ?? '',
        poster: r.poster,
        srcPath: r.src_path,
        posterPath: r.poster_path,
        updatedAt: r.updated_at,
        updatedBy: r.updated_by,
    };
}

// ── Reads ───────────────────────────────────────────────────────────────────

/**
 * Every tutorial row. Throws on failure rather than returning [] — an empty
 * array and "the query failed" render identically ("no videos yet"), and the
 * admin panel already has several places where that confusion is a live bug.
 * Callers decide whether to fall back; this function does not decide for them.
 *
 * Rows for topics no longer in HELP_TOPICS are dropped: a topic removed from
 * the code has no button left to open it, so showing it in the panel would
 * invite uploading a video nobody can ever reach.
 */
export async function fetchHelpVideos(): Promise<HelpVideoRow[]> {
    const { data, error } = await supabase
        .from('help_videos')
        .select('topic, title, src, poster, src_path, poster_path, updated_at, updated_by');

    if (error) throw new Error(error.message);
    return (data ?? [])
        .filter((r: HelpVideoDbRow) => KNOWN_TOPICS.has(r.topic))
        .map(toRow);
}

// ── Writes ──────────────────────────────────────────────────────────────────

/**
 * Upsert + read back. `.select().single()` is the load-bearing part: it turns an
 * RLS denial from a silent no-op into a thrown error the UI can show.
 *
 * Upsert rather than update because a topic added to HELP_TOPICS after this
 * migration ran has no row yet, and the operator should not have to run SQL to
 * publish its first video.
 */
async function upsertRow(topic: HelpTopic, patch: Record<string, unknown>): Promise<HelpVideoRow> {
    const { data, error } = await supabase
        .from('help_videos')
        .upsert({ topic, ...patch }, { onConflict: 'topic' })
        .select('topic, title, src, poster, src_path, poster_path, updated_at, updated_by')
        .single();

    if (error) throw new Error(error.message);
    if (!data) {
        // Belt and braces: .single() normally errors when zero rows come back,
        // but a null here would otherwise crash on the next property access
        // with a message that says nothing about permissions.
        throw new HelpVideoError('no-permission');
    }
    return toRow(data as HelpVideoDbRow);
}

function assertFile(file: File, maxBytes: number, accepted: readonly string[]): void {
    if (file.size === 0) {
        throw new HelpVideoError('file-empty');
    }
    if (file.size > maxBytes) {
        throw new HelpVideoError('file-too-big', {
            tamano: formatMb(file.size),
            limite: `${Math.round(maxBytes / (1024 * 1024))} MB`,
        });
    }
    // Some browsers report an empty type for uncommon containers. Fall back to
    // the extension rather than rejecting a file the operator can see is valid.
    if (file.type && !accepted.includes(file.type)) {
        throw new HelpVideoError('file-type', { tipo: file.type });
    }
}

export function formatMb(bytes: number): string {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Remove an object, tolerating "already gone". A failed cleanup must never
 *  fail the operation the operator actually asked for — the row is already
 *  correct at this point, and a leaked object is a housekeeping problem, not a
 *  broken tutorial. */
async function removeObject(path: string | null | undefined): Promise<void> {
    if (!path) return;
    const { error } = await supabase.storage.from(BUCKET).remove([path]);
    if (error) console.warn('[helpVideos] could not delete old object', path, error.message);
}

/**
 * Undo an upload whose row write threw — but ONLY once we have confirmed the
 * row does not actually reference the file.
 *
 * The naive version (delete unconditionally) is wrong for one specific failure:
 * the write COMMITS and the response is lost on the way back. supabase-js
 * reports a dropped connection the same shape as an RLS denial, so from here
 * the two are indistinguishable. Deleting on the optimistic assumption would
 * leave `src` pointing at an object that no longer exists — a tutorial that is
 * broken for every user and shows a green "Published" badge to the operator.
 *
 * So we re-read. An orphaned object costs a few megabytes and can be swept up
 * later; a dangling URL is a silent, user-visible break. When even the re-read
 * fails we keep the file, for the same reason.
 */
async function cleanUpAfterFailedWrite(topic: HelpTopic, uploadedPath: string): Promise<void> {
    const now = await currentPaths(topic);
    if (!now.ok) {
        console.warn('[helpVideos] could not verify the row; keeping', uploadedPath);
        return;
    }
    if (now.srcPath === uploadedPath || now.posterPath === uploadedPath) {
        console.warn('[helpVideos] write reported an error but committed; keeping', uploadedPath);
        return;
    }
    await removeObject(uploadedPath);
}

/**
 * Upload (or replace) the video for a topic.
 *
 * Order matters: upload → point the row at the new file → delete the old file.
 * Deleting first would leave the tutorial broken for everyone if the upload
 * then failed; this order's worst case is one orphaned object.
 */
/**
 * Upload (or replace) the tutorial video for a topic.
 *
 * RESUMABLE, not a plain `storage.upload()`. These recordings are now around
 * 500 MB, and a single request holding that much body dies whole on one
 * dropped connection — the operator would re-upload from zero, on hotel wifi,
 * with no progress bar. TUS chunks it and resumes.
 *
 * Returns a handle rather than a promise so the panel can show progress and
 * offer a cancel. The row is only pointed at the new file once the bytes have
 * actually landed.
 */
export function uploadHelpVideo(
    topic: HelpTopic,
    file: File,
    onProgress: (sent: number, total: number) => void = () => { },
): UploadHandle {
    assertFile(file, MAX_VIDEO_BYTES, VIDEO_MIME_TYPES);
    const path = uniqueObjectPath(topic, 'video', file, 'mp4');
    const transfer = resumableUpload(BUCKET, path, file, onProgress, 'video/mp4');

    const done = (async () => {
        // Read the existing paths BEFORE the upload lands so a replace knows
        // what to clean up, but after validation so a rejected file costs
        // nothing.
        const previous = await currentPaths(topic);
        try {
            await transfer.done;
        } catch (err) {
            // Re-badge the shared upload failures as this module's codes, so the
            // panel keeps switching on one error type instead of three.
            if (err instanceof UploadTooLargeForServer) throw new HelpVideoError('server-limit');
            if (err instanceof UploadNotSignedIn) throw new HelpVideoError('not-signed-in');
            throw err;  // UploadAborted included: the caller checks for it by type.
        }

        let row: HelpVideoRow;
        try {
            row = await upsertRow(topic, { src: publicUrl(path), src_path: path });
        } catch (err) {
            await cleanUpAfterFailedWrite(topic, path);
            throw err;
        }

        if (previous.srcPath && previous.srcPath !== path) await removeObject(previous.srcPath);
        return row;
    })();

    return { abort: transfer.abort, done };
}

/** What uploadHelpVideo hands back: a cancellable transfer whose promise
 *  resolves with the saved row. */
export interface UploadHandle {
    abort: () => void;
    done: Promise<HelpVideoRow>;
}

/** Optional still frame shown before playback. Without one the box renders a
 *  black rectangle until the user hits play. */
export async function uploadHelpVideoPoster(topic: HelpTopic, file: File): Promise<HelpVideoRow> {
    assertFile(file, MAX_POSTER_BYTES, POSTER_MIME_TYPES);

    const previous = await currentPaths(topic);
    const path = uniqueObjectPath(topic, 'poster', file, 'png');

    const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false });
    if (upErr) throw new Error(upErr.message);

    let row: HelpVideoRow;
    try {
        row = await upsertRow(topic, { poster: publicUrl(path), poster_path: path });
    } catch (err) {
        await cleanUpAfterFailedWrite(topic, path);
        throw err;
    }

    if (previous.posterPath && previous.posterPath !== path) await removeObject(previous.posterPath);
    return row;
}

/**
 * Clear the video for a topic: the button stays and reverts to "coming soon".
 *
 * The row is kept rather than deleted so the title survives — re-uploading
 * later should not mean re-typing it. Deleting the row would also drop it below
 * the seeded set, and nothing recreates it.
 */
export async function deleteHelpVideo(topic: HelpTopic): Promise<HelpVideoRow> {
    const previous = await currentPaths(topic);
    const row = await upsertRow(topic, { src: '', src_path: null });
    await removeObject(previous.srcPath);
    return row;
}

export async function deleteHelpVideoPoster(topic: HelpTopic): Promise<HelpVideoRow> {
    const previous = await currentPaths(topic);
    const row = await upsertRow(topic, { poster: null, poster_path: null });
    await removeObject(previous.posterPath);
    return row;
}

/** Rename the tutorial. This string is the heading in the floating player, so
 *  it is what the user reads while watching — not an internal label. */
export async function renameHelpVideo(topic: HelpTopic, title: string): Promise<HelpVideoRow> {
    const clean = title.trim();
    if (!clean) throw new HelpVideoError('title-empty');
    if (clean.length > MAX_TITLE_LENGTH) throw new HelpVideoError('title-too-long', { max: MAX_TITLE_LENGTH });
    return upsertRow(topic, { title: clean });
}

// ── Internals ───────────────────────────────────────────────────────────────

function publicUrl(path: string): string {
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return data.publicUrl;
}

/**
 * Read the storage paths currently referenced by a topic, so a replace knows
 * what to clean up. Read fresh from the DB rather than trusting whatever the
 * open tab last rendered — two admins editing at once would otherwise have the
 * second one delete a file the first had just replaced it with.
 */
async function currentPaths(
    topic: HelpTopic,
): Promise<{ ok: boolean; srcPath: string | null; posterPath: string | null }> {
    const { data, error } = await supabase
        .from('help_videos')
        .select('src_path, poster_path')
        .eq('topic', topic)
        .maybeSingle();

    // A missing row is normal for a topic that has never had a video, and reads
    // as ok with nulls. A failed QUERY is different and must stay
    // distinguishable: cleanUpAfterFailedWrite treats "I could not check" as a
    // reason to keep the file, and collapsing both into nulls would make it
    // delete precisely when it cannot verify — the case it exists to protect.
    if (error) {
        console.warn('[helpVideos] could not read existing paths:', error.message);
        return { ok: false, srcPath: null, posterPath: null };
    }
    return { ok: true, srcPath: data?.src_path ?? null, posterPath: data?.poster_path ?? null };
}
